<template>
  <v-form ref="timelineSegmentEditorForm">
    <dialog-base
      v-bind:shows="shows"
      v-bind:initialize-callback="$_onInitialize"
      v-bind:ok-callback="$_onOkClicked"
      v-bind:finalize-callback="$_onFinalize"
      v-bind:ok-disabled="$data.$_hasError"
    >
      <template v-slot:body>
        <v-card-title>Edit Timeline Segment</v-card-title>

        <v-card-text class="ma-0 pa-0">
          <v-container fluid>
            <v-row justify="center" align-content="center" no-gutters>
              <v-col cols="1">
                <timeline-segment-editor-side-button
                  v-bind:disabled="!reopenPreviousSegmentEnabled"
                  v-on:click="$_reopenPreviousTimelineSegment"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </timeline-segment-editor-side-button>
              </v-col>

              <v-col cols="10">
                <v-container fluid>
                  <v-row>
                    <v-col>
                      <component
                        v-if="$_segmentContentDataInputDefinition !== undefined"
                        v-model="$data.$_segmentData"
                        v-bind:is="$_segmentContentDataInputDefinition.componentName"
                        v-bind:is-immutable="isImmutable"
                        v-bind:timeline-segment-id="targetTimelineSegmentId"
                        v-bind:begin="$_segmentBegin"
                        v-bind:end="$_segmentEnd"
                        v-on:input="$_validateInput"
                      >
                      </component>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="6">
                      <general-text-field
                        label="Begin time in millisecond"
                        dense type="number"
                        v-bind:disabled="isImmutable"
                        v-bind:rules="$_rules.begin"
                        v-model.number="$data.$_beginMsec"
                        v-on:input="$_validateInput"
                      >
                      </general-text-field>
                    </v-col>

                    <v-col cols="6">
                      <general-text-field
                        label="End time in millisecond"
                        dense type="number"
                        v-bind:disabled="isImmutable"
                        v-bind:rules="$_rules.end"
                        v-model.number="$data.$_endMsec"
                        v-on:input="$_validateInput"
                      >
                      </general-text-field>
                    </v-col>
                  </v-row>
                </v-container>

                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header>Notes</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div class="timeline-segment-editor-add-note-container">
                        <v-btn
                          outlined text x-small
                          v-on:click="$_addNote"
                        >
                          Add Note
                        </v-btn>
                      </div>
                      <timeline-segment-editor-note
                        v-for="(noteWithInfoReferingToTargetSegment, noteWithInfoReferingToTargetSegmentIdx) of $data.$_noteWithInfoReferingToTargetSegmentArray"
                        v-bind:key="noteWithInfoReferingToTargetSegmentIdx"
                        v-bind:note-timeline-segment-id="noteWithInfoReferingToTargetSegment.noteTimelineSegmentId"
                        v-bind:note="noteWithInfoReferingToTargetSegment.note"
                        v-on:save-note="$_saveNote"
                      >
                      </timeline-segment-editor-note>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>

              <v-col cols="1">
                <timeline-segment-editor-side-button
                  v-bind:disabled="!reopenNextSegmentEnabled"
                  v-on:click="$_reopenNextTimelineSegment"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </timeline-segment-editor-side-button>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </template>
    </dialog-base>
  </v-form>
</template>

<style scoped>
div.timeline-segment-editor-add-note-container {
  text-align: right;
}
</style>

<script>
import { TimelineDataSet, TimelineSegmentDataBase, TimelineSegmentId } from '../../modules/ScattData.js';
import DialogBase from './DialogBase.vue';
import TimelineSegmentEditorNote from './TimelineSegmentEditor/TimelineSegmentEditorNote.vue';
import TimelineSegmentEditorSideButton from './TimelineSegmentEditor/TimelineSegmentEditorSideButton.vue';
import NoteSegmentData from '../../modules/segmentDataTypes/NoteSegmentData.js';
import referencePool from '../../modules/referencePool.js';
import NoteSegmentContentDataInput from '../Parts/Inputs/NoteSegmentContentDataInput.vue';
import LabelSegmentContentDataInput from '../Parts/Inputs/LabelSegmentContentDataInput.vue';
import MarkerSegmentContentDataInput from '../Parts/Inputs/MarkerSegmentContentDataInput.vue';
import GeneralSegmentContentDataInput from '../Parts/Inputs/GeneralSegmentContentDataInput.vue';
import GeneralTextField from '../Parts/Inputs/GeneralTextField.vue';
import TimeValue from '../../modules/TimeValue.js';

function findNoteTimelineSegmentIdsRefferingTo(targetTimelineSegmentId) {
  let noteTimelineSegmentIds = [];
  for (let reference of Object.values(referencePool.getAllReferences())) {
    if (reference.referTo.isSameAs(targetTimelineSegmentId) && (reference.refClass === 'Note')) {
      noteTimelineSegmentIds.push(reference.referFrom);
    }
  }
  return noteTimelineSegmentIds;
}

class NoteReferingToTargetSegmentWithInfo {
  static name = 'NoteReferingToTargetSegmentWithInfo';

  constructor(note, noteTimelineSegmentId) {
    this.note = note;
    this.noteTimelineSegmentId = noteTimelineSegmentId;
  }
}

export default {
  components: {
    DialogBase,
    TimelineSegmentEditorNote,
    TimelineSegmentEditorSideButton,
    NoteSegmentContentDataInput,
    LabelSegmentContentDataInput,
    MarkerSegmentContentDataInput,
    GeneralSegmentContentDataInput,
    GeneralTextField,
  },

  props: {
    shows:                              { type: Boolean },
    okCallback:                         { type: Function },
    finalizeCallback:                   { type: Function },
    timelineDataSet:                    { type: TimelineDataSet },
    minimumSegmentDurationMsec:         { type: Number,  default: 0 },
    targetSegment:                      { type: TimelineSegmentDataBase },
    targetTimelineSegmentId:            { type: TimelineSegmentId },
    isImmutable:                        { type: Boolean, default: false },
    reopenPreviousSegmentEnabled:       { type: Boolean, default: false },
    reopenNextSegmentEnabled:           { type: Boolean, default: false },
    segmentContentDataInputDefinitions: { type: Array,   default: () => ([]) },
  },

  data() {
    return {
      $_beginMsec: undefined,
      $_endMsec: undefined,
      $_segmentData: undefined,
      $_finalizeCallbackReturnValue: undefined,
      $_hasError: false,
      $_noteWithInfoReferingToTargetSegmentArray: [],
    };
  },

  computed: {
    $_segmentContentDataInputDefinition() {
      if (!this.shows) return undefined;
      for (let segmentContentDataInputDefinition of this.segmentContentDataInputDefinitions) {
        if (this.$data.$_segmentData instanceof segmentContentDataInputDefinition.segmentContentDataType) {
          return segmentContentDataInputDefinition;
        }
      }
      return undefined;
    },

    $_rules() {
      return {
        begin: [
          mustBeZeroOrAboveWhenNotEmpty,
          beginTimeMustBeLessThanEndTime.bind(this),
          beginTimeMustBeLessThanEndTimeMinusMinimumDuration.bind(this),
        ],
        end: [
          mustBeZeroOrAboveWhenNotEmpty,
          endTimeMustBeMoreThanBeginTime.bind(this),
          endTimeMustBeMoreThanBeginTimePlusMinimumDuration.bind(this),
        ],
      };

      function mustBeZeroOrAboveWhenNotEmpty(numberOrStringTime) {
        let stringValue = String(numberOrStringTime);
        if (stringValue.length > 0) {
          return (Number(stringValue) >= 0) || 'Must be 0 or above.';
        } else {
          return true;
        }
      }

      function eitherBeginTimeOrEndTimeEmpty(beginTime, endTime) {
        return (beginTime === '') || (endTime === '');
      }

      function beginTimeMustBeEqualToOrLessThanEndTimeMinusMinimumDuration(beginTime, endTime) {
        return (beginTime <= (endTime - this.minimumSegmentDurationMsec));
      }

      function beginTimeMustBeLessThanEndTime(numberOrStringBeginTime) {
        if (eitherBeginTimeOrEndTimeEmpty(numberOrStringBeginTime, this.$data.$_endMsec)) return true;
        let beginTime = Number(numberOrStringBeginTime);
        let endTime = Number(this.$data.$_endMsec);
        return (beginTime < endTime) || 'Begin time must be less than end time.';
      }

      function beginTimeMustBeLessThanEndTimeMinusMinimumDuration(numberOrStringBeginTime) {
        if (eitherBeginTimeOrEndTimeEmpty(numberOrStringBeginTime, this.$data.$_endMsec)) return true;
        let beginTime = Number(numberOrStringBeginTime);
        let endTime = Number(this.$data.$_endMsec);
        return beginTimeMustBeEqualToOrLessThanEndTimeMinusMinimumDuration.call(this, beginTime, endTime) || `Segment duration is too short(min: ${this.minimumSegmentDurationMsec} milliseconds)`;
      }

      function endTimeMustBeMoreThanBeginTime(numberOrStringEndTime) {
        if (eitherBeginTimeOrEndTimeEmpty(this.$data.$_beginMsec, numberOrStringEndTime)) return true;
        let beginTime = Number(this.$data.$_beginMsec);
        let endTime = Number(numberOrStringEndTime);
        return (beginTime < endTime);
      }

      function endTimeMustBeMoreThanBeginTimePlusMinimumDuration(numberOrStringEndTime) {
        if (eitherBeginTimeOrEndTimeEmpty(this.$data.$_beginMsec, numberOrStringEndTime)) return true;
        let beginTime = Number(this.$data.$_beginMsec);
        let endTime = Number(numberOrStringEndTime);
        return beginTimeMustBeEqualToOrLessThanEndTimeMinusMinimumDuration.call(this, beginTime, endTime);
      }
    },

    $_segmentBegin() {
      return (this.$data.$_beginMsec === '')? null : TimeValue.msec(Number(this.$data.$_beginMsec));
    },

    $_segmentEnd() {
      return (this.$data.$_endMsec === '')? null : TimeValue.msec(Number(this.$data.$_endMsec));
    },
  },

  watch: {
    timelineDataSet: {
      handler() { 
        this.$_updateNoteWithInfoReferingToTargetSegmentArray();
      },
      deep: true,
      immediate: true,
    },

    targetTimelineSegmentId: {
      handler() { 
        this.$_updateNoteWithInfoReferingToTargetSegmentArray();
      },
      deep: true,
      immediate: true,
    },

    targetSegment: {
      handler() {
        this.$_onInitialize();
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.$nextTick(this.$_validateInput);
  },

  inject: [
    'setSegment',
    'generateAndAttachEmptyNote',
    'selectPreviousTimelineSegment',
    'selectNextTimelineSegment',
    'invokeActionOnSelectedTimelineSegments',
  ],

  methods: {
    /* private */
    $_onInitialize() {
      this.$data.$_beginMsec = (this.targetSegment.begin === null)? '' : this.targetSegment.begin.msec;
      this.$data.$_endMsec = (this.targetSegment.end === null)? '' : this.targetSegment.end.msec;
      this.$data.$_segmentData = this.targetSegment.data.clone();
      this.$data.$_finalizeCallbackReturnValue = undefined;
    },

    $_onOkClicked() {
      this.$data.$_finalizeCallbackReturnValue = this.targetTimelineSegmentId;
      let segmentTemp = this.targetSegment.clone(true);
      segmentTemp.begin = this.$_segmentBegin;
      segmentTemp.end = this.$_segmentEnd;
      segmentTemp.data = this.$data.$_segmentData.clone();
      this.okCallback(segmentTemp);
    },

    $_onFinalize() {
      this.finalizeCallback(this.$data.$_finalizeCallbackReturnValue);
    },

    async $_reopenPreviousTimelineSegment() {
      this.$_onOkClicked();
      this.selectPreviousTimelineSegment();
      await this.invokeActionOnSelectedTimelineSegments('editSegment');
    },

    async $_reopenNextTimelineSegment() {
      this.$_onOkClicked();
      this.selectNextTimelineSegment();
      await this.invokeActionOnSelectedTimelineSegments('editSegment');
    },

    $_addNote() {
      this.generateAndAttachEmptyNote(this.targetTimelineSegmentId, this.$_segmentBegin, this.$_segmentEnd);
    },

    $_updateNoteWithInfoReferingToTargetSegmentArray() {
      this.$data.$_noteWithInfoReferingToTargetSegmentArray.splice(0);
      if (this.targetTimelineSegmentId !== undefined) {
        let noteTimelineSegmentIdsReferingToTargetSegment = findNoteTimelineSegmentIdsRefferingTo(this.targetTimelineSegmentId);
        for (let noteTimelineSegmentId of noteTimelineSegmentIdsReferingToTargetSegment) {
          let noteTimelineDataId = noteTimelineSegmentId.timelineDataId;
          let noteSegmentId = noteTimelineSegmentId.segmentId;
          let noteSegment = this.timelineDataSet[noteTimelineDataId].segments[noteSegmentId];
          let note = noteSegment.data.text;
          this.$data.$_noteWithInfoReferingToTargetSegmentArray.push(new NoteReferingToTargetSegmentWithInfo(note, noteTimelineSegmentId));
        }
      }
    },

    $_saveNote(noteDataTimelineSegmentId, description) {
      let noteDataTimelineDataId = noteDataTimelineSegmentId.timelineDataId;
      let noteDataSegmentId = noteDataTimelineSegmentId.segmentId;
      let newNoteData = new NoteSegmentData.contentData(description, new Date());
      let noteDataSegment = this.timelineDataSet[noteDataTimelineDataId].segments[noteDataSegmentId];
      let newNoteDataSegment = noteDataSegment.clone();
      newNoteDataSegment.data = newNoteData;
      this.setSegment(noteDataTimelineSegmentId, NoteSegmentData, newNoteDataSegment, 'Note set.', 'setting note');
    },

    $_validateInput() {
      this.$data.$_hasError = !this.$refs.timelineSegmentEditorForm.validate();
    },
  },
}
</script>